import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import './statistics.css';

import ClientRankingStat from './ClientRankingStat';
import OnboardingStat from './OnboardingStat';
import StaffHoursStat from './StaffHoursStat';
import CoverageStat from './CoverageStat';
import PaymentsStat from './PaymentsStat';
import LeadsStat from './LeadsStat';
import PropertiesStat from './PropertiesStat';

const STAT_CARDS = [
  {
    key: 'properties',
    title: 'Property Insights',
    desc: 'Buildings and unit sizes by area, how often each kind of unit turns over, and which areas, sizes and buildings work hardest on Airbnb.',
    badge: 'Market',
  },
  {
    key: 'client_ranking',
    title: 'Client Ranking',
    desc: 'Average bookings per day in a selected period. Groups clients by contribution.',
    badge: 'Operations',
  },
  {
    key: 'onboarding',
    title: 'Onboarding Trends',
    desc: 'Weekly onboarding for clients and properties with simple charts.',
    badge: 'Growth',
  },
  {
    key: 'cleaner_hours',
    title: 'Staff Working Hours',
    desc: 'Hours per staff per day with weekly totals — cleaners from bookings and reports, drivers from clock on/off.',
    badge: 'Workforce',
  },
  {
    key: 'coverage',
    title: 'Coverage Map',
    desc: 'Every zone we cover with the real community boundaries, and every active property pinned.',
    badge: 'Planning',
  },
  {
    key: 'leads',
    title: 'Leads',
    desc: 'Inquiries by date, the funnel to registered and booked, sources, types and why leads were lost.',
    badge: 'Growth',
  },
  {
    // MANAGERS ONLY (owner, 2026-09-09) — takings are not for supervisors or
    // admins. The server refuses everyone else; the card simply is not shown.
    key: 'payments',
    title: 'Payments',
    desc: 'Money in by day or week — card online, bank transfers, Zoho-recorded — this period vs the last, devices and saved cards.',
    badge: 'Money',
    roles: ['manager'],
  },
];

export default function Statistics() {
  const [searchParams, setSearchParams] = useSearchParams();

  const activeKey = searchParams.get('view'); // null => dashboard
  const role = (localStorage.getItem('userRole') || '').toLowerCase();
  const cards = useMemo(() => STAT_CARDS.filter((c) => !c.roles || c.roles.includes(role)), [role]);
  const activeCard = useMemo(() => {
    return cards.find((c) => c.key === activeKey) || null;
  }, [cards, activeKey]);

  const openView = (key) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('view', key);
      return next;
    });
  };

  const goBack = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('view');
      return next;
    });
  };

  return (
    <div className="statistics-container">
      <div className="statistics-page-header">
        <div>
          <h1 className="statistics-title">Statistics</h1>
          <p className="statistics-subtitle">
            Choose a report below. More statistics will be added over time.
          </p>
        </div>

        {activeKey ? (
          <button type="button" className="statistics-back-button" onClick={goBack}>
            ← Back to dashboard
          </button>
        ) : null}
      </div>

      {!activeKey && (
        <div className="statistics-dashboard">
          {cards.map((card) => (
            <button
              key={card.key}
              type="button"
              className={`statistics-dashboard-card ${card.disabled ? 'is-disabled' : ''}`}
              onClick={() => {
                if (card.disabled) return;
                openView(card.key);
              }}
            >
              <div className="statistics-dashboard-card-top">
                <div className="statistics-dashboard-card-title">{card.title}</div>
                <span className="statistics-dashboard-chip">{card.badge}</span>
              </div>

              <div className="statistics-dashboard-card-desc">{card.desc}</div>

              <div className="statistics-dashboard-card-footer">
                {card.disabled ? (
                  <span className="statistics-dashboard-muted">Coming soon</span>
                ) : (
                  <span className="statistics-dashboard-open">Open →</span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {activeKey && (
        <div className="statistics-active">
          {/* Staff hours carries its own toolbar — a second title band above
              it was pure duplication (2026-08-04). */}
          {activeKey !== 'cleaner_hours' && (
            <div className="statistics-active-header">
              <div className="statistics-active-title">{activeCard?.title || 'Statistics'}</div>
              <div className="statistics-active-sub">{activeCard?.desc || ''}</div>
            </div>
          )}

          {activeKey === 'properties' && <PropertiesStat />}
          {activeKey === 'client_ranking' && <ClientRankingStat />}
          {activeKey === 'onboarding' && <OnboardingStat />}
          {activeKey === 'cleaner_hours' && <StaffHoursStat />}
          {activeKey === 'coverage' && <CoverageStat />}
          {activeKey === 'leads' && <LeadsStat />}
          {activeKey === 'payments' && activeCard && <PaymentsStat />}
        </div>
      )}
    </div>
  );
}
